import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CustomerService } from 'app/shared/services/customer.service';
import { LocationService } from 'app/shared/services/location.service';
import {
    FlexDiagnosticCommandResponse,
    MonitorDiagnosticLog,
    MonitorDiagnosticLogsRequest,
    MonitorDiagnosticsService,
} from 'app/shared/services/monitor-diagnostics.service';
import { MonitorSettingService } from 'app/shared/services/monitor-setting.service';
import { SignalRService } from 'app/shared/services/signalr.service';
import { ViewDataService } from 'app/shared/services/view-data.service';
import { UnitsService } from 'app/shared/services/units.service';
import { ApiUnitsRow, UnitCategoryType } from 'app/shared/models/units';
import type { FlexMonitorStatusResponse } from 'app/shared/models/flex-monitor-status';
import type { LocationDetails } from 'app/shared/models/location-details';
import type { OptimizedViewData } from 'app/shared/models/view-data';
import type { SignalRMessage } from 'app/shared/models/signalr-message';
import type { FlexMonitorConfiguration } from 'app/shared/components/location-card/store/configs/flex/models';
import type { SensorConfigurationCollection } from 'app/shared/components/location-card/store/models/dynamic-section-models';
import { FlexMonitorStatusDataSource } from './flex-monitor-status.data-source';
import type { FlexDateContext, FlexInstallationDrawingData } from './flex-monitor-status.models';

@Injectable()
export class FlexMonitorStatusApiService extends FlexMonitorStatusDataSource {
    constructor(
        private monitorDiagnosticsService: MonitorDiagnosticsService,
        private monitorSettingService: MonitorSettingService,
        private locationService: LocationService,
        private viewDataService: ViewDataService,
        private customerService: CustomerService,
        private unitsService: UnitsService,
        private signalRService: SignalRService,
    ) {
        super();
    }

    public getStatus(customerId: number, locationId: number): Observable<FlexMonitorStatusResponse> {
        return this.monitorDiagnosticsService.getFlexMonitorStatus(customerId, locationId);
    }

    public getConfiguration(customerId: number, locationId: number): Observable<FlexMonitorConfiguration> {
        return this.monitorSettingService.getFlexConfiguration(customerId, locationId);
    }

    public getLocationDetails(customerId: number, locationId: number): Observable<LocationDetails[]> {
        return this.locationService.getLocationDetailsV2(customerId, locationId, true);
    }

    public getSensors(deviceId: string, customerId: number): Observable<SensorConfigurationCollection> {
        return this.locationService.getSensorConfigurationCollection(deviceId, customerId);
    }

    public getLatestHydrograph(
        customerId: number,
        locationId: number,
        entityIds: number[],
    ): Observable<OptimizedViewData | null> {
        return this.viewDataService.getLatestHydrograph(customerId, locationId, entityIds);
    }

    public getInstallationDrawing(customerId: number, locationId: number): Observable<FlexInstallationDrawingData> {
        return forkJoin({
            cosmos: this.locationService.locationCosmoDetails(customerId, locationId),
            units: this.unitsService.getUnitsCategories(customerId, [UnitCategoryType.Linear, UnitCategoryType.Area]),
        });
    }

    public getDateContext(customerId: number): Observable<FlexDateContext> {
        const current = this.customerService.customer;
        const customer$ =
            current?.customerID === customerId ? of(current) : this.customerService.getCustomerById(customerId);

        return customer$.pipe(
            switchMap((customer) =>
                this.customerService.getTimeZone().pipe(
                    map((timeZones) => ({
                        customer,
                        timeZone: timeZones.find((timeZone) => timeZone.value === customer.timeZone) ?? null,
                    })),
                ),
            ),
        );
    }

    public getDiagnosticUnits(customerId: number): Observable<ApiUnitsRow[]> {
        return this.unitsService.getUnitsCategories(customerId, [
            UnitCategoryType.Linear,
            UnitCategoryType.Velocity,
            UnitCategoryType.Temperature,
        ]);
    }

    public queueDiagnostics(
        customerId: number,
        locationId: number,
    ): Observable<FlexDiagnosticCommandResponse[] | null> {
        return this.monitorDiagnosticsService.requestDiagnostics({
            customerID: customerId,
            locationID: locationId,
            readings: [],
        });
    }

    public getDiagnosticLogs(
        customerId: number,
        locationId: number,
        request: MonitorDiagnosticLogsRequest,
    ): Observable<MonitorDiagnosticLog[] | null> {
        return this.monitorDiagnosticsService.getDiagnosticLogs(customerId, locationId, request);
    }

    public getDiagnosticNotifications(): Observable<SignalRMessage> {
        return this.signalRService.messageReceived;
    }
}
